import PartnerDividends from 'modules/partners/components/PartnerDividends';
import DetailsProfileSection from 'modules/_shared/components/Sections/DetailsProfileSection';
import { useGetPartnersSectionPermissions } from 'modules/partners/hooks/useGetPartnerSectionsPermissions';
import { checkIsPartnerCoOwner } from 'modules/partners/utils/checkIsPartnerCoOwner';

import { useTranslate } from 'hooks/useTranslate';
import { usePartnerSociety } from './hooks/usePartnerSociety';
import PartnerSocietyMetrics from './components/PartnerSocietyMetrics';
import PartnerSocietyPercentage from './components/PartnerSocietyPercentage';
import PartnerSocietySharesDetails from './components/PartnerSocietySharesDetails';
import PartnerSocietyTableWrapper from './components/PartnerSocietyTableWrapper';
import PartnerSocietySharesOffers from './components/PartnerSocietySharesOffers';
import { useGetCoOwnerShares } from './components/PartnerSocietySharesDetails/hooks/useGetCoOwnerShares';
import { PartnerDividendsProvider } from '../PartnerDividends/context/PartnerDividendsContext';

function PartnerSociety({
  partner,
  society,
  inversion,
  badges,
  isLoading,
  fetchPartnerData,
}) {
  const { t } = useTranslate();
  const { isAdmin, isReadOnly } = useGetPartnersSectionPermissions().data;
  const isCoOwner = checkIsPartnerCoOwner(partner);

  const {
    data: {
      fullName,
      hasSellSharesEnabled,
      hasBuySharesEnabled,
      hasSharesTitlesEnabled,
      isReadOnlyUser,
      decimalScale,
      NDpercent,
      FDpercent,
      PFDPercent,
    },
    actions: { handleOpenBuyShares, handleOpenSellShares },
  } = usePartnerSociety({
    partner,
    society,
    isReadOnly,
  });

  const useGetCoOwnerSharesResult = useGetCoOwnerShares({
    partnerId: partner?._id,
    isCoOwner,
  });

  const { coOwnerShares } = useGetCoOwnerSharesResult.data;

  return (
    <div className="card-inner-group">
      <DetailsProfileSection
        name={fullName}
        image={society.additional.logo}
        badges={badges}
        description=""
      />

      <PartnerSocietySharesOffers
        isReadOnly={isReadOnly}
        isReadOnlyUser={isReadOnlyUser}
        hasBuySharesEnabled={hasBuySharesEnabled}
        hasSellSharesEnabled={hasSellSharesEnabled}
        handleOpenBuyShares={handleOpenBuyShares}
        handleOpenSellShares={handleOpenSellShares}
      />

      <PartnerSocietyMetrics
        partner={partner}
        society={society}
        inversion={inversion}
        fullName={fullName}
        decimalScale={decimalScale}
        NDpercent={NDpercent}
        isAdmin={isAdmin}
        isLoading={isLoading}
      />

      <PartnerSocietyPercentage
        decimalScale={decimalScale}
        NDpercent={NDpercent}
        FDpercent={FDpercent}
        PFDPercent={PFDPercent}
        society={society}
        isAdmin={isAdmin}
      />

      <PartnerSocietyTableWrapper title={t('ParticipationDetail')}>
        <PartnerSocietySharesDetails
          partnerId={partner?._id}
          coOwnerShares={coOwnerShares}
          totalItems={partner?.sharesCount?.ranges}
          isAdmin={isAdmin}
          isCoOwner={false}
          hasSharesTitlesEnabled={hasSharesTitlesEnabled}
          fetchPartnerData={fetchPartnerData}
        />
      </PartnerSocietyTableWrapper>

      {isCoOwner && (
        <PartnerSocietyTableWrapper title={t('CoOwnershipDetail')}>
          <PartnerSocietySharesDetails
            partnerId={partner?._id}
            coOwnerShares={coOwnerShares}
            totalItems={partner?.sharesCount?.ranges}
            isAdmin={isAdmin}
            isCoOwner
            hasSharesTitlesEnabled={hasSharesTitlesEnabled}
          />
        </PartnerSocietyTableWrapper>
      )}

      <PartnerSocietyTableWrapper title={t('DividendsDistribution')}>
        <PartnerDividendsProvider
          partner={partner}
          showTitle={false}
          isSummaryView
        >
          <PartnerDividends />
        </PartnerDividendsProvider>
      </PartnerSocietyTableWrapper>
    </div>
  );
}

export default PartnerSociety;
